import type {
  BBox,
  AOIImportResponse,
  DisplacementComponent,
  DisplacementJobStatusResponse,
  DisplacementJobSubmitResponse,
  DisplacementResponse,
  HealthResponse,
  IndexResponse,
  IndexType,
  InterferogramResponse,
  LocalAOI,
  LocalAOIsResponse,
  LngLat,
  MaterialCategory,
  PolygonTimeSeriesResponse,
  RadarPointsResponse,
  RGBComposition,
  RGBResponse,
  ScenesResponse,
  SbasPlanResponse,
  SbasRunPackageResponse,
  StaticProductExportResponse,
  StoredProductsResponse,
  TimeSeriesResponse,
  UmbraSbasPlanResponse,
} from '../types';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const SBAS_API = `/api/${['sent', 'inel-sbas'].join('')}`;

/** Resolve a backend-relative URL (e.g. /storage/overlays/x.png) to a full one. */
export function resolveUrl(path: string): string {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  if (!path.startsWith('/')) return path;
  return `${BASE}${path}`;
}

class APIError extends Error {
  constructor(public status: number, public body: string, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new APIError(res.status, body, `${res.status} ${res.statusText} — ${body}`);
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

/** Download a POST response as a file. */
async function postDownload(path: string, body: unknown, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new APIError(res.status, text, `${res.status} — ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}${path}`, { method: 'POST', body: form });
  if (!res.ok) {
    const body = await res.text();
    throw new APIError(res.status, body, `${res.status} ${res.statusText} - ${body}`);
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => request<HealthResponse>('/api/health'),

  importKml: (file: File) => upload<AOIImportResponse>('/api/aoi/import-kml', file),

  localAois: () => request<LocalAOIsResponse>('/api/aoi/local'),

  localAoi: (aoiId: string) => request<LocalAOI>(`/api/aoi/local/${encodeURIComponent(aoiId)}`),

  searchScenes: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    max_cloud_cover: number;
  }) => post<ScenesResponse>('/api/scenes/search', params),

  computeIndex: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    index: IndexType;
    material: MaterialCategory;
  }) => post<IndexResponse>('/api/analysis/index', params),

  computeRGB: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    composition: RGBComposition;
  }) => post<RGBResponse>('/api/analysis/rgb', params),

  computeVelocity: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
  }) => post<DisplacementResponse>('/api/displacement/velocity', params),

  submitVelocityJob: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
    threshold_cm_yr?: number;
    color_max_cm_yr?: number;
    filter_below_threshold?: boolean;
  }) => post<DisplacementJobSubmitResponse>('/api/displacement/velocity/jobs', params),

  velocityJobStatus: (jobId: string) =>
    request<DisplacementJobStatusResponse>(`/api/displacement/velocity/jobs/${encodeURIComponent(jobId)}`),

  storedProducts: () => request<StoredProductsResponse>('/api/displacement/products'),

  renderStoredProduct: (localId: string, params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
    threshold_cm_yr?: number;
    color_max_cm_yr?: number;
    filter_below_threshold?: boolean;
  }) => post<DisplacementResponse>(`/api/displacement/products/${encodeURIComponent(localId)}/render`, params),

  storedProductPolygon: (localId: string, params: {
    coordinates: LngLat[];
    component: DisplacementComponent;
    start_date: string;
    end_date: string;
  }) => post<PolygonTimeSeriesResponse>(`/api/displacement/products/${encodeURIComponent(localId)}/polygon`, params),

  storedProductRadarPoints: (localId: string, params: {
    bbox: BBox;
    component: DisplacementComponent;
    max_points?: number;
  }) => post<RadarPointsResponse>(
    `/api/displacement/products/${encodeURIComponent(localId)}/radar-points`,
    { max_points: 30000, ...params },
  ),

  exportStaticStoredProduct: (localId: string, params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
    threshold_cm_yr?: number;
    color_max_cm_yr?: number;
    filter_below_threshold?: boolean;
  }) => post<StaticProductExportResponse>(
    `/api/displacement/products/${encodeURIComponent(localId)}/static-export`,
    params,
  ),

  umbraAuthStatus: () => request<{
    provider: 'UMBRA';
    has_credentials: boolean;
    auth_mode: 'access_token' | 'client_credentials' | 'none';
  }>('/api/commercial-sar/umbra/auth/status'),

  planUmbraSbas: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    target_resolution_m?: number;
    revisit_days?: number;
    min_acquisitions?: number;
    max_temporal_baseline_days?: number;
    submit_to_provider?: boolean;
  }) => post<UmbraSbasPlanResponse>('/api/commercial-sar/umbra/sbas/plan', {
    target_resolution_m: 2.5,
    revisit_days: 12,
    min_acquisitions: 12,
    max_temporal_baseline_days: 24,
    submit_to_provider: false,
    ...params,
  }),

  umbraSbasProducts: () => request<StoredProductsResponse>('/api/commercial-sar/umbra/products'),

  renderUmbraSbasProduct: (localId: string, params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
  }) => post<DisplacementResponse>(`/api/commercial-sar/umbra/products/${encodeURIComponent(localId)}/render`, params),

  planSbas: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    target_resolution_m?: number;
    profile?: 'OPERATIONAL_10M' | 'PREMIUM_5M' | 'EXPERIMENTAL_2P5M';
    max_temporal_baseline_days?: number;
    min_acquisitions?: number;
    processor?: 'ISCE_MINTPY' | 'SNAP_MINTPY' | 'GMTSAR_MINTPY';
  }) => post<SbasPlanResponse>(`${SBAS_API}/plan`, {
    target_resolution_m: 5,
    profile: 'PREMIUM_5M',
    max_temporal_baseline_days: 48,
    min_acquisitions: 18,
    processor: 'ISCE_MINTPY',
    ...params,
  }),

  sbasProducts: () => request<StoredProductsResponse>(`${SBAS_API}/products`),

  packageSbasPlan: (planId: string) =>
    post<SbasRunPackageResponse>(`${SBAS_API}/plans/${encodeURIComponent(planId)}/package`, {}),

  renderSbasProduct: (localId: string, params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
  }) => post<DisplacementResponse>(`${SBAS_API}/products/${encodeURIComponent(localId)}/render`, params),

  psSbasProducts: () => request<StoredProductsResponse>('/api/ps-sbas/products'),

  renderPsSbasProduct: (productId: string, params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    component: DisplacementComponent;
    decompose: boolean;
  }) => post<DisplacementResponse>(`/api/ps-sbas/products/${encodeURIComponent(productId)}/render`, params),

  psSbasRadarPoints: (productId: string, params: {
    bbox: BBox;
    component: DisplacementComponent;
    max_points?: number;
  }) => post<RadarPointsResponse>(
    `/api/ps-sbas/products/${encodeURIComponent(productId)}/radar-points`,
    { max_points: 20000, ...params },
  ),

  psSbasPolygon: (productId: string, params: {
    coordinates: LngLat[];
    component: DisplacementComponent;
    start_date: string;
    end_date: string;
  }) => post<PolygonTimeSeriesResponse>(`/api/ps-sbas/products/${encodeURIComponent(productId)}/polygon`, params),

  computeInterferogram: (params: {
    bbox: BBox;
    start_date: string;
    end_date: string;
    pair_index: number;
  }) => post<InterferogramResponse>('/api/displacement/interferogram', params),

  radarPoints: (params: {
    bbox: BBox;
    component: DisplacementComponent;
    max_points?: number;
  }) => post<RadarPointsResponse>('/api/radar-points', { max_points: 850, ...params }),

  timeseries: (params: {
    point_id: string;
    start: string;
    end: string;
    component: DisplacementComponent;
  }) => {
    const q = new URLSearchParams({
      start: params.start,
      end: params.end,
      component: params.component,
    });
    return request<TimeSeriesResponse>(
      `/api/radar-points/${encodeURIComponent(params.point_id)}/timeseries?${q}`,
    );
  },

  polygonAnalyze: (params: {
    coordinates: LngLat[];
    component: DisplacementComponent;
    start_date: string;
    end_date: string;
  }) => post<PolygonTimeSeriesResponse>('/api/polygon/analyze', params),

  exportPolygonCSV: (params: {
    coordinates: LngLat[];
    component: DisplacementComponent;
    start_date: string;
    end_date: string;
  }) => postDownload('/api/polygon/export/csv', params, 'puntos_radar_poligono.csv'),

  exportTimeseriesCSV: (params: {
    coordinates: LngLat[];
    component: DisplacementComponent;
    start_date: string;
    end_date: string;
  }) => postDownload('/api/polygon/export/timeseries-csv', params, 'serie_temporal_poligono.csv'),
};

export { APIError };
